import { LogOutIcon } from '@/components/icons'
import { Button } from '@/components/ui/button'
import {
  Menu,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuTrigger,
} from '@/components/ui/menu'
import { UserAvatar } from '@/components/ui/user-avatar'
import { useSignOut } from '@/lib/use-sign-out'
import { cn } from '@/lib/utils'

export type AdminSessionUser = {
  name?: string | null
  email?: string | null
  image?: string | null
}

type AdminAccountMenuProps = {
  user: AdminSessionUser
  /** Icon-only trigger when the sidebar is collapsed on desktop. */
  collapsed?: boolean
}

/**
 * Staff identity + sign-out for the ops sidebar (desktop icon rail + mobile sheet).
 */
export function AdminAccountMenu({
  user,
  collapsed = false,
}: AdminAccountMenuProps) {
  const { signOut, isSigningOut } = useSignOut()
  const displayName = user.name?.trim() || user.email || 'Staff'
  const secondary = user.name?.trim() && user.email ? user.email : null

  return (
    <Menu>
      <MenuTrigger
        render={
          <Button
            type="button"
            variant="outline"
            size={collapsed ? 'icon' : 'sm'}
            className={cn(
              'w-full justify-start gap-2 px-2',
              collapsed && 'size-8 justify-center px-0',
            )}
          />
        }
        aria-label="Account menu"
      >
        <UserAvatar
          src={user.image}
          name={user.name}
          email={user.email}
          className="size-6"
          alt=""
        />
        {!collapsed ? (
          <span className="min-w-0 flex-1 truncate text-left text-xs font-medium">
            {displayName}
          </span>
        ) : null}
      </MenuTrigger>
      <MenuPopup align="start" side="top" className="min-w-56">
        <div className="flex flex-col gap-0.5 px-2 py-1.5">
          <p className="truncate text-sm font-medium text-foreground">
            {displayName}
          </p>
          {secondary ? (
            <p className="truncate text-xs text-muted-foreground">
              {secondary}
            </p>
          ) : null}
        </div>
        <MenuSeparator />
        <MenuItem
          variant="destructive"
          disabled={isSigningOut}
          onClick={() => {
            void signOut()
          }}
        >
          <LogOutIcon />
          {isSigningOut ? 'Signing out…' : 'Sign out'}
        </MenuItem>
      </MenuPopup>
    </Menu>
  )
}
